import { PropsWithChildren, useMemo } from "react";
import omit from "lodash/omit";
import { MenuItem } from "@blueprintjs/core";
import {
  IMultiSelectProps,
  ISelectProps,
  ItemPredicate,
  ItemRenderer,
  MultiSelect,
  Select,
} from "@blueprintjs/select";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type Extractor<T> = (item: T) => string;

type Props<T> = PropsWithChildren<
  Omit<
    ISelectProps<T>,
    "itemRenderer" | "itemPredicate" | "itemListPredicate"
  > & {
    extractor: Extractor<T>;
    isItemSelected?: (item: T) => boolean;
    isItemLocked?: (item: T) => boolean;
    disabled?: boolean;
  }
>;

type MultiProps<T> = PropsWithChildren<
  Omit<
    IMultiSelectProps<T>,
    "itemRenderer" | "itemPredicate" | "itemListPredicate" | "tagRenderer"
  > & {
    extractor: Extractor<T>;
    isItemSelected: (item: T) => boolean;
    isItemLocked?: (item: T) => boolean;
    disabled?: boolean;
    itemPredicate?: ItemPredicate<T>; // itemPredicate is the same as in original, only here it is optional
  }
>;

const filterItem = <T,>(extractor: Extractor<T>): ItemPredicate<T> => {
  return (query: string, item: T) => extractor(item).includes(query);
};

export const renderItem = <T,>(
  extractor: Extractor<T>,
  isItemSelected?: (item: T) => boolean,
  isItemLocked?: (item: T) => boolean
): ItemRenderer<T> => {
  return (item: T, { modifiers, handleClick }) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }
    return (
      <MenuItem
        data-testid={
          `menu-item-${extractor(item)
            .split(" ")
            .join("-")
            .replace(/[\u200B-\u200D\uFEFF]/g, "")}` // remove zero-width space if applicable
        }
        active={modifiers.active}
        icon={
          isItemSelected ? (isItemSelected(item) ? "tick" : "blank") : undefined
        }
        labelElement={
          isItemLocked && isItemLocked(item) ? (
            <FontAwesomeIcon icon={faLock} />
          ) : null
        }
        key={extractor(item)}
        onClick={handleClick}
        text={extractor(item)}
        shouldDismissPopover={false}
        disabled={isItemLocked?.(item)}
        style={{ pointerEvents: "initial" }} // Fix for jest testing bug
      />
    );
  };
};

export const MenuSelect = <T,>(props: Props<T>) => {
  const selectProps = omit(props, "extractor");
  const Component = useMemo(() => Select.ofType<T>(), []);
  return (
    <Component
      {...{
        ...selectProps,
        itemRenderer: renderItem(
          props.extractor,
          props.isItemSelected,
          props.isItemLocked
        ),
        itemPredicate: filterItem(props.extractor),
      }}
    />
  );
};

export const MenuMultiSelect = <T,>(props: MultiProps<T>) => {
  const selectProps = omit(props, "extractor");
  const Component = useMemo(() => MultiSelect.ofType<T>(), []);
  return (
    <Component
      {...{
        ...selectProps,
        itemRenderer: renderItem(
          props.extractor,
          props.isItemSelected,
          props.isItemLocked
        ),
        itemPredicate: props.itemPredicate || filterItem(props.extractor),
        tagRenderer: props.extractor,
      }}
    />
  );
};
